import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { getAssessments } from "@/lib/api";
import { Section, Eyebrow, DisclaimerNote } from "@/components/Primitives";

const PATHWAYS = [
  {
    id: "personal",
    icon: "fa-seedling",
    title: "I want to change something in my own life",
    blurb: "For people who feel stuck in recurring habits, behaviors, emotional responses, or patterns and want to understand their readiness for change.",
    button: "Explore personal assessments",
    items: [
      { id: "tenet-readiness", recommended: true, desc: "Explore where you are now, what may be getting in the way, and how ready you feel to make meaningful change." },
      { id: "sacred-flame-readiness", desc: "For those who want to change unwanted pornography or sexual behavior and understand their readiness for deeper work." },
      { id: "recovery-environment", desc: "Explore whether your routines, environment, relationships, and daily structure support the change you want." },
    ],
  },
  {
    id: "support",
    icon: "fa-people-roof",
    title: "Someone I love is struggling",
    blurb: "For partners, parents, and family members trying to understand how to support someone without losing themselves in the process.",
    button: "Explore support assessments",
    items: [
      { id: "partner-impact", desc: "How another person's behavior may be affecting your emotional wellbeing, trust, daily life, and stability." },
      { id: "enabling-vs-supporting", desc: "Whether your efforts to help support healthy change or may unintentionally reduce accountability." },
      { id: "caregiver-burnout", desc: "Whether supporting someone has led to exhaustion, resentment, preoccupation, or loss of your own needs." },
      { id: "family-support-readiness", desc: "How ready and resourced you feel to support a loved one well." },
      { id: "family-system-impact", desc: "How the situation may be affecting parenting, finances, household roles, and family functioning." },
    ],
  },
  {
    id: "relationship",
    icon: "fa-heart",
    title: "My relationship has been affected",
    blurb: "For people dealing with broken trust, difficult communication, damaged connection, or questions about rebuilding.",
    button: "Explore relationship assessments",
    items: [
      { id: "trust-repair", desc: "The current condition of trust and what may need attention before meaningful rebuilding can occur." },
      { id: "communication-under-stress", desc: "What tends to happen in hard conversations — avoidance, escalation, defensiveness, shutdown, or constructive dialogue." },
      { id: "reconciliation-readiness", desc: "Whether both people may be ready to begin rebuilding, rather than simply wanting the conflict to stop." },
      { id: "boundary-clarity", desc: "Where your boundaries may be weak, inconsistent, punitive, or unclear — and where clearer limits may help." },
    ],
  },
];

export default function AssessmentHub() {
  const [byId, setById] = useState({});
  const [disclaimer, setDisclaimer] = useState("");
  const [open, setOpen] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    getAssessments().then((d) => {
      setById(Object.fromEntries(d.assessments.map((a) => [a.id, a])));
      setDisclaimer(d.disclaimer);
    });
  }, []);

  const allAssessments = useMemo(() => Object.values(byId), [byId]);

  const renderItem = (item) => {
    const meta = byId[item.id];
    if (!meta) return null;
    return (
      <Link
        key={item.id}
        to={`/assessments/${item.id}`}
        data-testid={`assessment-card-${item.id}`}
        className="group bg-white rounded-2xl border border-stone-200 p-6 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col"
      >
        {item.recommended && (
          <span className="self-start bg-[#C9A962]/20 text-[#8B6F3D] text-xs font-semibold px-3 py-1 rounded-full mb-3">
            <i className="fa-solid fa-star mr-1.5" />Recommended starting point
          </span>
        )}
        <h4 className="font-playfair text-lg text-stone-900 leading-snug">{meta.title}</h4>
        <p className="text-stone-600 text-sm mt-2 leading-relaxed flex-1">{item.desc || meta.description}</p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-stone-100">
          <span className="text-xs text-stone-500">{meta.duration}</span>
          <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] group-hover:gap-3 transition-all">
            Start assessment <i className="fa-solid fa-arrow-right text-xs" />
          </span>
        </div>
      </Link>
    );
  };

  return (
    <div className="pb-8">
      <Section className="pt-14 sm:pt-20">
        {/* Section 1 — intro */}
        <div className="max-w-2xl">
          <Eyebrow>Free self-assessments</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-stone-900 leading-[1.08] mt-4">
            Find the assessment that fits where you are right now
          </h1>
          <p className="text-lg text-stone-600 leading-relaxed mt-5">
            You don't need to know exactly what kind of help you need before you begin. Choose the
            situation that feels closest to what you're experiencing, and we'll help you find the most
            relevant assessment.
          </p>
        </div>

        {/* Not sure? Start here */}
        <div className="mt-8 rounded-[2rem] bg-[#1F3D2E] p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="start-here-banner">
          <div className="flex-1">
            <p className="text-amber-300/80 font-mono text-xs tracking-[0.2em] uppercase">Not sure where to begin?</p>
            <h2 className="font-playfair text-2xl text-amber-50 mt-2">Start with the Transformation Readiness Assessment</h2>
            <p className="text-stone-300 text-sm mt-2 max-w-xl">
              It's designed to help you understand where you are now and what kind of next step may make sense. You don't have to choose perfectly.
            </p>
          </div>
          <Link
            to="/assessments/tenet-readiness"
            data-testid="start-here-cta"
            className="shrink-0 rounded-full px-7 py-4 text-sm font-semibold bg-amber-500 text-stone-900 hover:bg-amber-400 transition-colors text-center"
          >
            Start here
          </Link>
        </div>

        {/* Section 2 — three pathway cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {PATHWAYS.map((p) => (
            <button
              key={p.id}
              data-testid={`path-${p.id}`}
              onClick={() => { setOpen(open === p.id ? null : p.id); setShowAll(false); }}
              className={`text-left bg-white rounded-2xl border p-7 transition-all duration-300 flex flex-col ${
                open === p.id ? "border-[#1F3D2E] ring-2 ring-[#1F3D2E]/20 shadow-xl" : "border-stone-200 hover:-translate-y-1 hover:shadow-xl"
              }`}
            >
              <div className="grid place-items-center h-12 w-12 rounded-xl bg-[#1F3D2E]/10">
                <i className={`fa-solid ${p.icon} text-[#1F3D2E] text-lg`} />
              </div>
              <h3 className="font-playfair text-xl text-stone-900 mt-5 leading-snug">{p.title}</h3>
              <p className="text-stone-600 text-sm mt-2 leading-relaxed flex-1">{p.blurb}</p>
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-5">
                {p.button}
                <i className={`fa-solid fa-chevron-${open === p.id ? "up" : "down"} text-xs`} />
              </span>
            </button>
          ))}
        </div>

        {/* Section 3 — expanded category */}
        <AnimatePresence>
          {open && !showAll && (
            <motion.div
              key={open}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
              className="overflow-hidden"
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid={`category-${open}`}>
                {PATHWAYS.find((p) => p.id === open).items.map(renderItem)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Level 3 — full library */}
        <div className="mt-12 text-center border-t border-stone-200 pt-8">
          <button
            data-testid="view-all-toggle"
            onClick={() => { setShowAll((v) => !v); setOpen(null); }}
            className="text-sm font-semibold text-stone-500 hover:text-[#1F3D2E]"
          >
            {showAll ? "Hide full library" : "View all assessments"}
            <i className={`fa-solid fa-chevron-${showAll ? "up" : "down"} text-xs ml-2`} />
          </button>
        </div>

        <AnimatePresence>
          {showAll && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8"
              data-testid="all-assessments-grid"
            >
              {allAssessments.map((meta) => (
                <Link
                  key={meta.id}
                  to={`/assessments/${meta.id}`}
                  data-testid={`assessment-card-${meta.id}`}
                  className="group bg-white rounded-2xl border border-stone-200 p-6 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <span className="text-xs font-mono uppercase tracking-wider text-[#8B6F3D]">{meta.category}</span>
                  <h4 className="font-playfair text-lg text-stone-900 mt-3 leading-snug">{meta.title}</h4>
                  <p className="text-stone-600 text-sm mt-2 leading-relaxed flex-1">{meta.description}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F3D2E] mt-4 group-hover:gap-3 transition-all">
                    Start assessment <i className="fa-solid fa-arrow-right text-xs" />
                  </span>
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {disclaimer && <DisclaimerNote className="mt-10 max-w-2xl" text={disclaimer} />}
      </Section>
    </div>
  );
}
